import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErroApi, api } from '../api';
import { useSessao } from '../sessao';
import { porNome } from '../formato';
import { Lixeira, useLixeira } from '../Lixeira';
import { Aviso, Campo, Carregando, ErroDaApi, Selo, Vazio } from '../ui';
import {
  FAMILIAS_CAT, FAMILIA_NOME, type Categoria,
} from './EquipamentoForms';
import { FAMILIAS } from './Equipamentos';

/**
 * O catálogo de categorias do parque — configuração, e não trabalho.
 *
 * ── Por que saiu de dentro do parque ──────────────────────────────────
 *
 * «Acredito que o cadastro de categorias de equipamentos deveria ser em
 *  outro local (com acesso apenas para administradores)» — Humberto,
 * 01/09/2026.
 *
 * A permissão já era só de administrador (`cadastro.categoria`, e só o
 * perfil `admin` a tem). O que estava errado era o LUGAR: uma janela
 * modal dentro da tela de busca do parque, aberta por um botão ao lado de
 * «Novo equipamento». Quem passa o dia procurando balança não deveria
 * esbarrar no catálogo que define o que é uma balança.
 *
 * O critério da casa já existia, e é o mesmo que tirou «Usuários e
 * acessos» do menu em 23/08: **no menu o que você FAZ; na engrenagem o
 * que você CONFIGURA.** A categoria não produz registro técnico e
 * ninguém a abre num turno normal de bancada — é engrenagem.
 *
 * ── E por que não é mais uma janela ───────────────────────────────────
 *
 * Porque são 38 linhas com rolagem própria, e uma janela modal engole o
 * clique de fora e o botão de voltar do navegador. Página tem endereço,
 * volta e histórico; janela não tem nenhum dos três.
 */
/**
 * Onde a categoria aparece.
 *
 * `equipamento` e `padrao` são valores de coluna, não texto para
 * alguém ler numa tabela — e são justamente o par que confunde, porque
 * padrão TAMBÉM é equipamento. O rótulo diz o que a escolha faz: em
 * qual das duas telas a categoria vai aparecer.
 */
/**
 * Como o objeto é identificado fisicamente (`0112`).
 *
 * «Cápsulas, anéis e cilindros não levam adesivo. Apenas equipamentos
 *  onde há superfície suficiente para colar um adesivo» — Humberto,
 * 01/09/2026.
 *
 * Marquei só as seis categorias que ele nomeou. As outras trinta ficam
 * em `adesivo`, que é o que o sistema já fazia — decidir por elas seria
 * eu dizendo o que tem superfície suficiente, e isso se decide com o
 * objeto na mão. É desta tela que elas se resolvem.
 */
const ETIQUETA: Record<string, string> = {
  adesivo: 'Plaqueta adesiva',
  gravado: 'Número gravado',
};

const PAPEL: Record<string, string> = {
  equipamento: 'Parque de equipamentos',
  padrao: 'Padrões de referência',
  componente: 'Sensor de máquina',
};

export function CategoriasDeEquipamento() {
  const { pode } = useSessao();
  const [lista, setLista] = useState<Categoria[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  const [nome, setNome] = useState('');
  const [codigo, setCodigo] = useState('');
  const [familia, setFamilia] = useState('instrumento');
  const [papel, setPapel] = useState<'equipamento' | 'padrao'>('equipamento');
  const [salvando, setSalvando] = useState(false);

  const comLixeira = useLixeira();
  const carregar = useCallback(() => {
    api<Categoria[]>('/equipamentos/categorias?todas=true')
      .then(setLista).catch(setErro);
  }, []);

  useEffect(carregar, [carregar]);

  /*
   * O código é DERIVADO do nome, e não digitado.
   *
   * Quem administra o parque escreve «Prensa de concreto»; o código
   * `prensa_concreto` sai daí. Pedir os dois é pedir que a pessoa
   * entenda a diferença entre o nome que aparece na tela e a chave que
   * vai para o banco — e é assim que nasce `prensaConcreto` ao lado de
   * `prensa_concreto`. O campo continua editável para o caso raro em que
   * a derivação não serve.
   */
  const derivar = (t: string) => t
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '').slice(0, 60);

  const codigoFinal = (codigo.trim() || derivar(nome)).trim();
  const invalido = nome.trim().length < 2
    || !/^[a-z][a-z0-9_]*$/.test(codigoFinal) || salvando;

  async function incluir() {
    setErro(null);
    setSalvando(true);
    try {
      await api('/equipamentos/categorias', {
        metodo: 'POST',
        corpo: { codigo: codigoFinal, nome: nome.trim(), papel, familia },
      });
      setNome(''); setCodigo('');
      carregar();
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  async function alternarEtiqueta(c: Categoria) {
    setErro(null);
    const vai = c.etiqueta === 'gravado' ? 'adesivo' : 'gravado';
    try {
      await api(`/equipamentos/categorias/${c.codigo}`, {
        metodo: 'PATCH',
        corpo: {
          etiqueta: vai,
          motivo: vai === 'gravado'
            ? 'Objeto sem superfície para plaqueta adesiva — número gravado'
            : 'Objeto com superfície suficiente para plaqueta adesiva',
        },
      });
      carregar();
    } catch (e) { setErro(e); }
  }

  /*
   * O prefixo do código (onda 210). A folha de aferição escreve só ele
   * na linha nova — «CAP-» — e a pessoa digita o número gravado.
   */
  async function gravarPrefixo(c: Categoria, valor: string) {
    const v = valor.trim().toUpperCase();
    if (v === (c.prefixo_codigo ?? '')) return;
    setErro(null);
    try {
      await api(`/equipamentos/categorias/${c.codigo}`, {
        metodo: 'PATCH',
        corpo: {
          prefixoCodigo: v,
          motivo: v ? `Prefixo do código passa a ser ${v}` : 'Prefixo do código retirado',
        },
      });
      carregar();
    } catch (e) { setErro(e); }
  }

  async function alternar(c: Categoria) {
    setErro(null);
    try {
      await api(`/equipamentos/categorias/${c.codigo}`, {
        metodo: 'PATCH',
        corpo: {
          ativo: !c.ativo,
          motivo: c.ativo
            ? 'Categoria aposentada pelo administrador do parque'
            : 'Categoria reativada pelo administrador do parque',
        },
      });
      carregar();
    } catch (e) { setErro(e); }
  }

  const administra = pode('cadastro.categoria');

  /*
   * Por família, e dentro dela em ordem alfabética (onda 352, RN-273).
   *
   * A 350 trouxe a «ordem da casa», com «Subir»/«Descer». O Humberto
   * decidiu em 23/10/2026: «o parque de equipamentos também deveria estar
   * em ordem alfabética» — e a mesma ordem vale aqui, no parque, no
   * relatório do parque e no «Novo equipamento».
   */
  const familias = useMemo(() => {
    /* A mesma ordem e o mesmo nome de família do parque e do relatório. */
    const ordemFam = new Map(FAMILIAS.map((f, i) => [f.codigo, i]));
    const grupos = new Map<string, Categoria[]>();
    for (const c of lista ?? []) {
      const f = c.familia ?? 'instrumento';
      if (!grupos.has(f)) grupos.set(f, []);
      grupos.get(f)!.push(c);
    }
    return [...grupos.entries()]
      .sort(([a], [b]) => (ordemFam.get(a) ?? 9) - (ordemFam.get(b) ?? 9))
      .map(([familia, itens]) => ({
        familia,
        itens: [...itens].sort((a, b) => porNome(a.nome, b.nome)),
      }));
  }, [lista]);

  if (erro && !lista) return <ErroDaApi erro={erro} />;
  if (!lista) return <Carregando texto="Carregando o catálogo" />;

  return (
    <div className="pagina">
      <div className="pagina-cabeca">
        <div className="linha-topo">
          <h1 className="cresce">Categorias do parque</h1>
          <Link className="botao secundario" to="/equipamentos">
            Ir para o parque
          </Link>
        </div>
        <p className="sub">
          O catálogo que define o que é uma balança, um molde ou um sensor.
          Por família e, dentro dela, em ordem alfabética — a mesma ordem
          do parque, do relatório do parque e do «Novo equipamento».
        </p>
      </div>

      <Aviso tipo="info" titulo="A lista é fechada de propósito.">
        Categoria digitada livremente produziu “balanca”, “balança” e
        “Balanças” como três categorias diferentes, e o filtro do parque
        passou a devolver um terço do que devia. Incluir aqui vale para todo
        mundo, com o mesmo nome.
      </Aviso>

      {!administra && (
        <Aviso tipo="atencao" titulo="Você pode ver, e não mexer">
          Mudar o catálogo é ato de administração: exige a permissão
          <b> cadastro.categoria</b>, que hoje só o perfil
          <b> Administrador</b> tem.
        </Aviso>
      )}

      <ErroDaApi erro={erro} />

      <div className="cartao">
        {lista.length === 0
          ? <Vazio>O catálogo está vazio.</Vazio>
          : (
            <div className="rola">
              <table className="dados">
                <thead>
                  <tr>
                    <th>Categoria</th>
                    <th>Família</th>
                    <th>Papel</th>
                    <th>Identificação</th>
                    <th>Código começa com</th>
                    <th className="num">Equipamentos</th>
                    <th>Situação</th>
                    {comLixeira && <th className="col-lixeira" aria-label="Excluir" />}
                  </tr>
                </thead>
                <tbody>
                  {familias.map((g) => (
                    <Fragment key={g.familia}>
                    <tr className="linha-familia">
                      <th colSpan={7 + (comLixeira ? 1 : 0)} scope="colgroup">
                        {FAMILIAS.find((f) => f.codigo === g.familia)?.nome
                          ?? FAMILIA_NOME[g.familia] ?? g.familia}
                      </th>
                    </tr>
                  {g.itens.map((c) => (
                    <tr key={c.codigo}>
                      <td>
                        {c.nome}
                        <div className="sub mono">{c.codigo}</div>
                      </td>
                      <td className="sub">
                        {FAMILIA_NOME[c.familia ?? 'instrumento'] ?? c.familia}
                      </td>
                      <td className="sub">{PAPEL[c.papel] ?? c.papel}</td>
                      <td>
                        {/*
                          * Decide se a folha de etiquetas inclui a
                          * categoria. É a diferença entre imprimir 50
                          * adesivos úteis e gastar a folha inteira em
                          * discos anelares que não têm onde colá-la.
                          */}
                        {administra
                          ? (
                            <button
                              type="button"
                              className="botao discreto"
                              onClick={() => { void alternarEtiqueta(c); }}
                              title={c.etiqueta === 'gravado'
                                ? 'Passar a incluir na folha de etiquetas'
                                : 'Tirar da folha de etiquetas'}
                            >
                              {ETIQUETA[c.etiqueta ?? 'adesivo'] ?? c.etiqueta}
                            </button>
                          )
                          : (ETIQUETA[c.etiqueta ?? 'adesivo'] ?? c.etiqueta)}
                      </td>
                      <td>
                        {administra
                          ? (
                            <input
                              key={`${c.codigo}-${c.prefixo_codigo ?? ''}`}
                              className="entrada-codigo mono"
                              type="text"
                              maxLength={20}
                              defaultValue={c.prefixo_codigo ?? ''}
                              placeholder="—"
                              aria-label={`Prefixo do código de ${c.nome}`}
                              onBlur={(e) => { void gravarPrefixo(c, e.currentTarget.value); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                              }}
                            />
                          )
                          : <span className="mono">{c.prefixo_codigo ?? '—'}</span>}
                      </td>
                      <td className="num mono">{c.quantos}</td>
                      <td>
                        {/*
                          * O estado e a ação na mesma célula: sem o selo,
                          * uma linha aposentada só se distinguia pelo
                          * texto do botão — que diz o que VAI acontecer, e
                          * não o que É.
                          */}
                        {!c.ativo && <Selo tom="neutro">Aposentada</Selo>}
                        {administra && (
                          <button
                            type="button"
                            className="botao discreto"
                            onClick={() => { void alternar(c); }}
                          >
                            {c.ativo ? 'Aposentar' : 'Reativar'}
                          </button>
                        )}
                      </td>
                      {comLixeira && (
                        <td className="col-lixeira">
                          <Lixeira tipo="categoria" id={c.codigo}
                            rotulo={`categoria ${c.nome}`} aoApagar={carregar} />
                        </td>
                      )}
                    </tr>
                  ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <p className="dica" style={{ marginTop: '.7rem' }}>
          Aposentar não apaga: a categoria continua valendo para os
          equipamentos que já a usam e some da lista de quem cadastra um novo.
        </p>
      </div>

      {administra && (
        <div className="cartao">
          <h2>Incluir categoria</h2>
          <Campo
            rotulo="Nome"
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
            erro={erro instanceof ErroApi ? erro.campo('nome') : undefined}
            dica="Como o laboratório chama. Ex.: Prensa de concreto."
          />
          <Campo
            rotulo="Código"
            value={codigo}
            onChange={(e) => setCodigo(e.currentTarget.value)}
            erro={erro instanceof ErroApi ? erro.campo('codigo') : undefined}
            placeholder={derivar(nome)}
            dica="Sai do nome sozinho. Só mexa se precisar — ele não muda depois."
          />
          <div className="linha">
            {/*
              * A família é o primeiro corte da busca do parque (`0096`).
              * Sem ela a categoria nova nasceria em `instrumento` por
              * omissão do banco, e um molde novo apareceria fora da aba
              * dos itens aferidos — que é onde quem procura vai olhar.
              */}
            <label className="campo">
              <span className="rotulo">Família</span>
              <select
                value={familia}
                onChange={(e) => setFamilia(e.currentTarget.value)}
              >
                {FAMILIAS_CAT.map((f) => (
                  <option key={f.codigo} value={f.codigo}>{f.nome}</option>
                ))}
              </select>
              <span className="dica">
                {FAMILIAS_CAT.find((f) => f.codigo === familia)?.sub}
              </span>
            </label>

            <label className="campo">
              <span className="rotulo">Onde aparece</span>
              <select
                value={papel}
                onChange={(e) => setPapel(e.currentTarget.value as 'equipamento' | 'padrao')}
              >
                <option value="equipamento">No parque de equipamentos</option>
                <option value="padrao">Na lista de padrões de referência</option>
              </select>
              <span className="dica">
                Padrão de referência tem tela própria porque afere os outros.
              </span>
            </label>
          </div>

          <button type="button" className="botao" disabled={invalido}
            onClick={() => { void incluir(); }}>
            {salvando ? 'Incluindo…' : 'Incluir'}
          </button>
        </div>
      )}
    </div>
  );
}
